import React, { useEffect, useState } from 'react';
import { X, ZoomIn, ZoomOut, RotateCcw, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import { membersAPI } from '../services/apiService';
import { mergeKycDocumentsToPdf, uint8ToPdfObjectUrl } from '../utils/mergeKycPdf';

const MemberKycPreview = ({ memberId, memberName, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [member, setMember] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [docCount, setDocCount] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;

    (async () => {
      setLoading(true);
      setError('');
      setPdfUrl(null);
      try {
        const res = await membersAPI.getById(memberId);
        if (cancelled) return;
        const m = res?.member;
        if (!m) {
          setError('Member not found');
          return;
        }
        setMember(m);

        const docs = { ...(m.kyc_documents || {}) };
        if (!docs.bank?.data && docs.ifsc?.data) docs.bank = docs.ifsc;
        const count = ['aadhar', 'pan', 'bank'].filter((k) => docs[k]?.data).length;
        setDocCount(count);

        if (!count) {
          setError('No KYC documents uploaded for this member');
          return;
        }

        const bytes = await mergeKycDocumentsToPdf({ ...m, kyc_documents: docs });
        if (cancelled) return;
        objectUrl = uint8ToPdfObjectUrl(bytes);
        setPdfUrl(objectUrl);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError(err?.message || 'Unable to build KYC PDF');
          toast.error(err?.message || 'Unable to build KYC PDF');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [memberId]);

  useEffect(() => () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
  }, [pdfUrl]);

  const adjustZoom = (delta) => {
    setZoom((z) => Math.min(2.5, Math.max(0.6, Number((z + delta).toFixed(2)))));
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-3 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-start justify-between gap-3 px-4 sm:px-5 py-3 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">KYC documents (combined PDF)</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {memberName || member?.name || 'Member'}
              {member?.uan ? ` · UAN ${member.uan}` : ''}
              {docCount ? ` · ${docCount} file(s) merged` : ''}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 sm:px-5 py-2 flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50 text-xs text-slate-600">
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            <span>DOB: <strong className="text-slate-900">{member?.date_of_birth || '—'}</strong></span>
            <span>Aadhar: <strong className="text-slate-900">{member?.aadhar_number || '—'}</strong></span>
            <span>PAN: <strong className="text-slate-900">{member?.pan_number || '—'}</strong></span>
            <span>Bank: <strong className="text-slate-900">{member?.bank_account || '—'}</strong></span>
            <span>IFSC: <strong className="text-slate-900">{member?.ifsc_code || '—'}</strong></span>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => adjustZoom(-0.15)} className="btn-secondary inline-flex items-center px-2 py-1 text-xs" title="Zoom out">
              <ZoomOut className="w-3.5 h-3.5" />
            </button>
            <span className="w-12 text-center">{Math.round(zoom * 100)}%</span>
            <button type="button" onClick={() => adjustZoom(0.15)} className="btn-secondary inline-flex items-center px-2 py-1 text-xs" title="Zoom in">
              <ZoomIn className="w-3.5 h-3.5" />
            </button>
            <button type="button" onClick={() => setZoom(1)} className="btn-secondary inline-flex items-center px-2 py-1 text-xs" title="Reset zoom">
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-auto bg-slate-100 p-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500 text-sm px-6 text-center">
              <FileText className="w-8 h-8 mb-2 opacity-50" />
              {error}
            </div>
          ) : !pdfUrl ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500 text-sm">
              <FileText className="w-8 h-8 mb-2 opacity-50" />
              No combined PDF available.
            </div>
          ) : (
            <div className="flex justify-center items-start min-h-[280px]">
              <div
                style={{ transform: `scale(${zoom})`, transformOrigin: 'top center' }}
                className="transition-transform"
              >
                <iframe
                  title="Combined KYC PDF"
                  src={pdfUrl}
                  className="w-[720px] max-w-[90vw] h-[70vh] bg-white rounded border border-slate-200"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MemberKycPreview;
